import { FC, useState } from 'react';
import { Formik } from 'formik';
import { Form } from 'react-router-dom';
import { Box, Checkbox, FormControl, FormControlLabel, FormGroup, Typography } from '@mui/material';
import InformationIcon from '../../../../../../../assets/icons/Info, Infornation.png';
import {
  useGetActivityInterestQuery,
  useUpdateUserAdditionalInfoMutation,
} from '../../../../../../../store/services/customizeOptionsApi';
import { useAppDispatch, useAppSelector } from '../../../../../../../hooks/redux';
import { setUserInfo } from '../../../../../../../store/slices/userSlice';
import CustomButton from '../../../../../../../common/CustomButton';

import styles from './Destinations.module.scss';

type FormikValues = {
  destinations?: string[];
};

type DestinationsProps = {
  closeModal: () => void;
};

const Destinations: FC<DestinationsProps> = ({ closeModal }) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(state => state.user);
  const [updateUserAdditionalInfo, { isSuccess }] = useUpdateUserAdditionalInfoMutation();
  const { data: typeOfDestination } = useGetActivityInterestQuery('destination_types');
  const { type: planType } = useAppSelector(state => state.plan);

  const [resErrors, setResErrors] = useState<{ attr: string | null; code: string; detail: string }[] | null>(null);

  const initialValues = {
    destinations: user.destination_types,
  };

  const handleSubmit = async (values: FormikValues) => {
    const payload = {
      destination_types: values.destinations,
    };

    try {
      await updateUserAdditionalInfo(payload);
      dispatch(setUserInfo(payload));
      setResErrors(null);
      closeModal();
    } catch (error) {
      console.error('Error updating user info:', error);
      setResErrors((error as any)?.data?.errors || null);
    }
  };

  const formikValidate = (values: FormikValues) => {
    const errors: FormikValues = {};

    return errors;
  };

  const availableOptionsCount = 6;

  return (
    <Box className={styles.destinations}>
      <Formik
        initialValues={initialValues}
        validate={formikValidate}
        onSubmit={handleSubmit}
      >
        {({ values, handleChange, handleSubmit, setFieldValue, isSubmitting }) => (
          <Form
            className={styles.destinationsForm}
            onSubmit={handleSubmit}
          >
            <FormControl
              component="fieldset"
              className={`${styles.radioGroupWrapper} ${styles.mt35}`}
            >
              <Typography
                variant="h5Bold"
                className={styles.stepTitle}
              >
                Let's personalize your AI assistant
              </Typography>
              <Typography
                variant="h10"
                className={styles.stepSubtitle}
              >
                Types Of Destinations (Select Upto {availableOptionsCount})
              </Typography>
              <FormGroup className={styles.checkboxesGroup}>
                {typeOfDestination?.map((option: { name: string; is_free: boolean }) => {
                  const isChecked = values.destinations.includes(option.name);
                  const reachMaxLimit = values.destinations.length >= availableOptionsCount && !isChecked;
                  const isDisabled = (planType.includes('Basic') || planType === '') && !option.is_free;
                  const finalDisabled = isDisabled || reachMaxLimit;

                  return (
                    <FormControlLabel
                      key={option.name}
                      className={`${styles.checkLabel} ${isChecked ? styles.checked : ''} ${finalDisabled ? styles.disabledOption : ''}`}
                      control={
                        <Checkbox
                          className={styles.checkbox}
                          checked={isChecked}
                          disabled={finalDisabled}
                          onChange={event => {
                            if (event.target.checked) {
                              setFieldValue(
                                'destinations',
                                !!values.destinations[0]?.length ? [...values.destinations, option.name] : [option.name]
                              );
                            } else {
                              setFieldValue(
                                'destinations',
                                values.destinations.filter((item: string) => item !== option.name)
                              );
                            }
                          }}
                        />
                      }
                      label={
                        <Typography
                          variant={isChecked ? 'h10Semibold' : 'h10'}
                          className={finalDisabled ? styles.disabledOptionText : ''}
                        >
                          {option.name}
                        </Typography>
                      }
                    />
                  );
                })}
              </FormGroup>
            </FormControl>

            <Box className={styles.additionalInfo}>
              <Typography
                variant="h11"
                className={styles.additionalInfoText}
              >
                To get badges, fill in all the questions
              </Typography>
              <img
                src={InformationIcon}
                className={styles.additionalInfoImg}
                alt="info"
              />
            </Box>

            <Box className={styles.cityAndAiroportBtns}>
              <CustomButton
                btnType="button"
                variant="outlined"
                onClick={closeModal}
              >
                Cancel
              </CustomButton>
              <CustomButton
                btnType="submit"
                customClass={styles.customButton}
                disabled={isSubmitting}
              >
                Save
              </CustomButton>
            </Box>
            {isSuccess && (
              <Typography
                variant="h12"
                sx={{ color: '#208B64' }}
              >
                Destinations updated successfully.
              </Typography>
            )}
            {resErrors &&
              resErrors.map((error, index) => (
                <Typography
                  key={index}
                  variant="h12"
                  sx={{ color: '#C8102E' }}
                >
                  {error.detail}
                </Typography>
              ))}
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default Destinations;
